import { amrapStopSentence } from '../amrapStopWords';
import type { DraftSet } from './state/loggerDraftTypes';

type T = (k: string, opts?: Record<string, unknown>) => string;

// #308: what each control on the logger card says when it is HELD (Antenore
// on v2: «tutti i bottoni ed elementi devono avere un hint che possono essere
// mantenuti in hold»). One place for the words, so the rows only wire them.
// The chips that already had a tip keep their key (BI/UNI, AMRAP, Burn+,
// Dropset, Myo-rep); the rest are new.
export function cardHints(t: T) {
  return {
    // A hold nobody is told about is a hold nobody has: said once per device
    // (useTransientTip's `intro`).
    intro: t('mobile.logger.holdHintIntro', {
      defaultValue: 'Hold any button to see what it does.',
    }),
    title: t('mobile.logger.hintTitle', {
      defaultValue: 'Tap to swap this exercise.',
    }),
    howTo: t('mobile.logger.hintHowTo', {
      defaultValue: 'How to do this exercise.',
    }),
    target: t('mobile.logger.hintTarget', {
      defaultValue: 'Sets in the last 7 days per muscle.',
    }),
    trash: t('mobile.logger.hintTrash', {
      defaultValue: 'Removes this exercise from the workout.',
    }),
    clock: t('mobile.logger.hintClock', {
      defaultValue: 'Past sets of this exercise.',
    }),
    superset: t('mobile.logger.hintSuperset', {
      defaultValue: 'Superset: pair with another exercise and alternate sets.',
    }),
    supersetCue: t('mobile.logger.hintSupersetCue', {
      defaultValue: 'Superset: tap to go to its next exercise.',
    }),
    supersetMenu: t('mobile.logger.hintSupersetMenu', {
      defaultValue: 'Superset options: add, reorder, remove or dissolve.',
    }),
    week: t('mobile.logger.hintWeek', {
      defaultValue: 'Tap for the next week. Swipe the grid to go back.',
    }),
    timed: t('mobile.logger.hintTimed', {
      defaultValue: 'Timed: this set is held for a time, not counted in reps.',
    }),
    assisted: t('mobile.logger.hintAssisted', {
      defaultValue: 'Assisted: a band or machine takes load off. Counts as minus.',
    }),
    note: t('mobile.logger.hintNote', {
      defaultValue: 'Note: write a cue for this set.',
    }),
    // #539: the target ring says whose target it is. Chip on: the AMRAP's,
    // in the plan's stop rule when there is one (effortTargetOf).
    effortTarget: (
      set: Pick<DraftSet, 'amrap' | 'amrapPrescribed' | 'amrapStopPrescribed'>,
    ) =>
      !set.amrap
        ? t('mobile.logger.hintEffortTarget', {
            defaultValue: 'Prescribed by the plan.',
          })
        : set.amrapPrescribed && set.amrapStopPrescribed
          ? amrapStopSentence(t, set.amrapStopPrescribed)
          : t('mobile.logger.hintEffortTargetAmrap', {
              defaultValue: 'AMRAP: the target is the last rep you can do.',
            }),
    dropsetRemove: t('mobile.logger.hintDropsetRemove', {
      defaultValue: 'Back to a single working set.',
    }),
    pin: (pinned: boolean) =>
      pinned
        ? t('mobile.logger.hintPinOn', {
            defaultValue: 'Pinned: stays in focus after the rest. Tap to unpin.',
          })
        : t('mobile.logger.hintPinOff', {
            defaultValue: 'Pin: keeps this exercise in focus when the rest ends.',
          }),
    laterality: (unilateral: boolean) =>
      t(
        unilateral
          ? 'mobile.logger.lateralityTipUni'
          : 'mobile.logger.lateralityTipBi',
      ),
    burnPlus: t('mobile.logger.tipBurnPlus'),
    dropset: t('workout.dropsetHintClassical'),
    myorep: t('workout.dropsetHintMyorep'),
    // Two real texts, the athlete's outcome and the coach's request, and one
    // place that picks between them (PE-42).
    amrap: (prescribing: boolean) =>
      t(prescribing ? 'mobile.logger.tipAmrapPrescribe' : 'mobile.logger.tipAmrap'),
  };
}

export type CardHints = ReturnType<typeof cardHints>;
